import { useState } from "react";
import { useLang } from "../i18n";
import { IMG } from "../content/images";
import { PageHero } from "../components/PageHero";
import { Seo } from "../components/Seo";
import { Turnstile } from "../components/Turnstile";
import { Field, Input, Textarea, Select, Consent, Honeypot, FormStatus } from "../components/forms";
import { SOLUTIONS } from "../content/solutions";
import { api, apiError } from "../lib/api";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const CONDITIONAL_KEYS = {
  "fibre-optique": "fibre",
  "internet-fixe-sans-fil": "wireless",
  "internet-satellite": "satellite",
  "ip-transit-dia": "iptransit",
  "genie-civil-fibre": "civil",
};

const Quote = () => {
  const { lang, t } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    customer_type: "", company: "", sector: "", service: "", service_details: "",
    province: "", city: "", address: "", sites: "",
    bandwidth: "", connection_type: "", redundancy: "", sla: "", public_ip: "",
    existing_provider: "", target_date: "", description: "", conditional_answer: "",
    full_name: "", role: "", email: "", phone: "", contact_method: "",
  });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ ok: false, msg: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const q = t.quote;
  const condKey = CONDITIONAL_KEYS[form.service] || (form.customer_type?.toLowerCase().includes(lang === "fr" ? "gouv" : "gov") ? "government" : null);

  const validateStep = () => {
    const errs = {};
    if (step === 0 && !form.customer_type) errs.customer_type = t.forms.required;
    if (step === 1 && !form.service) errs.service = t.forms.required;
    if (step === 4) {
      if (form.full_name.trim().length < 2) errs.full_name = t.forms.required;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t.forms.invalidEmail;
      if (form.phone.trim().length < 6) errs.phone = t.forms.required;
      if (!consent) errs.consent = t.forms.consentRequired;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => { if (validateStep()) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setBusy(true); setStatus({ ok: false, msg: "" });
    try {
      const fd = new FormData();
      const serviceTitle = SOLUTIONS.find((s) => s.slug === form.service)?.[lang]?.title || form.service;
      fd.append("payload", JSON.stringify({ ...form, service: serviceTitle, service_slug: form.service, locale: lang, consent, turnstile_token: token, honeypot }));
      if (file) fd.append("attachment", file);
      const res = await api.post("/public/quotes", fd);
      setStatus({ ok: true, msg: res.data.message || q.successMsg });
    } catch (err) { setStatus({ ok: false, msg: apiError(err, t.forms.error) }); }
    finally { setBusy(false); }
  };

  if (status.ok) {
    return (
      <div data-testid="quote-page">
        <Seo title={t.nav.quote} path="demander-un-devis" />
        <PageHero eyebrow="BULA TELECOMS SA" title={q.title} crumbs={[{ label: q.title }]} />
        <section className="bg-[#F5F5F3]">
          <div className="max-w-2xl mx-auto px-4 py-20 text-center">
            <span className="inline-flex w-16 h-16 rounded-full bg-[#FF6600] items-center justify-center"><Check size={30} className="text-[#08090A]" /></span>
            <p className="mt-6 text-lg font-bold text-[#101214]" role="status" data-testid="quote-success">{status.msg}</p>
          </div>
        </section>
      </div>
    );
  }

  const stepContent = [
    // 0 — Customer profile
    <div className="space-y-5" key="s0">
      <Field label={q.step1.customerType} required error={errors.customer_type} htmlFor="q-type">
        <Select id="q-type" data-testid="quote-customer-type" value={form.customer_type} onChange={set("customer_type")}>
          <option value="">—</option>
          {t.coverage.customerTypes.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </Field>
      <Field label={q.step1.company} htmlFor="q-company">
        <Input id="q-company" data-testid="quote-company-input" value={form.company} onChange={set("company")} />
      </Field>
      <Field label={q.step1.sector} htmlFor="q-sector">
        <Input id="q-sector" data-testid="quote-sector-input" value={form.sector} onChange={set("sector")} />
      </Field>
    </div>,
    // 1 — Service
    <div className="space-y-5" key="s1">
      <Field label={q.step2.service} required error={errors.service} htmlFor="q-service">
        <Select id="q-service" data-testid="quote-service-select" value={form.service} onChange={set("service")}>
          <option value="">—</option>
          {SOLUTIONS.map((s) => <option key={s.slug} value={s.slug}>{s[lang].title}</option>)}
        </Select>
      </Field>
      {condKey && (
        <Field label={q.conditional[condKey]} htmlFor="q-cond">
          <Input id="q-cond" data-testid="quote-conditional-input" value={form.conditional_answer} onChange={set("conditional_answer")} />
        </Field>
      )}
      <Field label={q.step2.details} htmlFor="q-svc-details">
        <Textarea id="q-svc-details" data-testid="quote-service-details" value={form.service_details} onChange={set("service_details")} />
      </Field>
    </div>,
    // 2 — Site
    <div className="space-y-5" key="s2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label={q.step3.province} htmlFor="q-province"><Input id="q-province" data-testid="quote-province-input" value={form.province} onChange={set("province")} /></Field>
        <Field label={q.step3.city} htmlFor="q-city"><Input id="q-city" data-testid="quote-city-input" value={form.city} onChange={set("city")} /></Field>
      </div>
      <Field label={q.step3.address} htmlFor="q-address"><Textarea id="q-address" rows={2} data-testid="quote-address-input" value={form.address} onChange={set("address")} /></Field>
      <Field label={q.step3.sites} htmlFor="q-sites"><Input id="q-sites" data-testid="quote-sites-input" value={form.sites} onChange={set("sites")} /></Field>
    </div>,
    // 3 — Technical
    <div className="space-y-5" key="s3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label={q.step4.bandwidth} htmlFor="q-bw"><Input id="q-bw" data-testid="quote-bandwidth-input" value={form.bandwidth} onChange={set("bandwidth")} placeholder="100 Mbps, 1 Gbps…" /></Field>
        <Field label={q.step4.connectionType} htmlFor="q-ct">
          <Select id="q-ct" data-testid="quote-connection-type" value={form.connection_type} onChange={set("connection_type")}>
            <option value="">—</option><option value="dedicated">{q.step4.dedicated}</option><option value="shared">{q.step4.shared}</option>
          </Select>
        </Field>
        <Field label={q.step4.redundancy} htmlFor="q-red"><Input id="q-red" data-testid="quote-redundancy-input" value={form.redundancy} onChange={set("redundancy")} /></Field>
        <Field label={q.step4.sla} htmlFor="q-sla"><Input id="q-sla" data-testid="quote-sla-input" value={form.sla} onChange={set("sla")} /></Field>
        <Field label={q.step4.publicIp} htmlFor="q-ip"><Input id="q-ip" data-testid="quote-publicip-input" value={form.public_ip} onChange={set("public_ip")} /></Field>
        <Field label={q.step4.existingProvider} htmlFor="q-provider"><Input id="q-provider" data-testid="quote-provider-input" value={form.existing_provider} onChange={set("existing_provider")} /></Field>
        <Field label={q.step4.targetDate} htmlFor="q-date"><Input id="q-date" type="date" data-testid="quote-target-date" value={form.target_date} onChange={set("target_date")} /></Field>
        <Field label={q.step4.attachment} htmlFor="q-file"><Input id="q-file" type="file" data-testid="quote-attachment-input" onChange={(e) => setFile(e.target.files?.[0] || null)} /></Field>
      </div>
      <Field label={q.step4.description} htmlFor="q-desc"><Textarea id="q-desc" rows={5} data-testid="quote-description-input" value={form.description} onChange={set("description")} /></Field>
    </div>,
    // 4 — Contact
    <div className="space-y-5" key="s4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label={q.step5.fullName} required error={errors.full_name} htmlFor="q-name"><Input id="q-name" data-testid="quote-name-input" value={form.full_name} onChange={set("full_name")} /></Field>
        <Field label={q.step5.role} htmlFor="q-role"><Input id="q-role" data-testid="quote-role-input" value={form.role} onChange={set("role")} /></Field>
        <Field label={q.step5.email} required error={errors.email} htmlFor="q-email"><Input id="q-email" type="email" data-testid="quote-email-input" value={form.email} onChange={set("email")} /></Field>
        <Field label={q.step5.phone} required error={errors.phone} htmlFor="q-phone"><Input id="q-phone" data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder="+243 …" /></Field>
      </div>
      <Field label={q.step5.contactMethod} htmlFor="q-method">
        <Select id="q-method" data-testid="quote-contact-method" value={form.contact_method} onChange={set("contact_method")}>
          <option value="">—</option>
          {t.coverage.contactMethods.map((m) => <option key={m} value={m}>{m}</option>)}
        </Select>
      </Field>
      <Consent checked={consent} onChange={setConsent} error={errors.consent} testid="quote-consent" />
      <Turnstile action="quote" onToken={setToken} />
      <p className="text-xs text-[#101214]/45">{t.forms.privacyNote}</p>
      <FormStatus ok={status.ok} msg={status.msg} testid="quote-form-status" />
    </div>,
  ];

  return (
    <div data-testid="quote-page">
      <Seo title={q.title} path="demander-un-devis" />
      <PageHero eyebrow="BULA TELECOMS SA" title={q.title} sub={q.subtitle} image={IMG.engineerPanel} crumbs={[{ label: q.title }]} />

      <section className="bg-[#F5F5F3]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          {/* Stepper */}
          <ol className="mb-10 flex items-center gap-1.5" data-testid="quote-stepper">
            {q.steps.map((s, i) => (
              <li key={s} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-[#FF6600]" : "bg-[#101214]/10"}`} />
                <p className={`mt-2 text-[10px] sm:text-xs font-bold ${i === step ? "text-[#FF6600]" : "text-[#101214]/40"}`}>{i + 1}. {s}</p>
              </li>
            ))}
          </ol>

          <form onSubmit={submit} className="bg-white border border-[#101214]/10 rounded-2xl p-7 sm:p-10" data-testid="quote-multi-step-form">
            <h2 className="text-xl font-bold text-[#101214] mb-6">{q.steps[step]}</h2>
            {stepContent[step]}
            <div className="mt-8 flex items-center justify-between gap-4">
              {step > 0 ? (
                <button type="button" onClick={() => setStep(step - 1)} data-testid="quote-back-button"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#101214]/60 hover:text-[#FF6600] transition-colors">
                  <ArrowLeft size={16} /> {q.back}
                </button>
              ) : <span />}
              {step < 4 ? (
                <button type="button" onClick={next} data-testid="quote-next-step-button"
                  className="inline-flex items-center gap-2 bg-[#101214] hover:bg-[#FF6600] text-white hover:text-[#08090A] font-black rounded-full px-7 py-3 text-sm transition-colors">
                  {q.next} <ArrowRight size={16} />
                </button>
              ) : (
                <button type="submit" disabled={busy} data-testid="quote-submit-button"
                  className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] disabled:opacity-50 text-[#08090A] font-black rounded-full px-8 py-3.5 text-sm transition-colors shadow-[0_0_20px_rgba(255,102,0,0.3)]">
                  {busy ? t.forms.sending : q.submit} <ArrowRight size={16} />
                </button>
              )}
            </div>
            <Honeypot value={honeypot} onChange={setHoneypot} />
          </form>
        </div>
      </section>
    </div>
  );
};

export default Quote;
